// Regras de apresentação do catálogo de notificações (estado, diff, ações permitidas). Puro, sem rede nem React, para ser testado.
// Quem decide de verdade o que é permitido é o servidor; aqui só se decide o que MOSTRAR e o que avisar antes de enviar.

export const STATE_LABELS = {
    draft: 'Rascunho',
    pending_approval: 'Aguardando aprovação',
    rejected: 'Rejeitada',
    scheduled: 'Agendada',
    published: 'Publicada',
    superseded: 'Substituída'
};

export const STATE_COLORS = {
    draft: 'default',
    pending_approval: 'warning',
    rejected: 'error',
    scheduled: 'info',
    published: 'success',
    superseded: 'default'
};

/** Estados em andamento: no máximo uma versão por item está num deles. */
export const IN_FLIGHT_STATES = ['draft', 'pending_approval', 'rejected', 'scheduled'];

/** Atributos que mudam a quem e como a pessoa é avisada: só o editor de consentimento os altera (D10.29, salvaguarda c). */
export const CONSENT_ATTRIBUTES = ['audience', 'category', 'consentRule', 'canDisable', 'channels'];

export const CONSENT_LABELS = {
    audience: 'Público',
    category: 'Categoria',
    consentRule: 'Regra de consentimento',
    canDisable: 'A pessoa pode desligar',
    channels: 'Canais'
};

export const inFlightVersion = (item) => item.versions.find((v) => IN_FLIGHT_STATES.includes(v.state)) ?? null;
export const activeVersion = (item) => item.versions.find((v) => v.id === item.activeVersionId) ?? null;

/** O que a lista mostra para um item. */
export const itemStatus = (item) => {
    if (item.isDeactivated) return { label: 'Desativado', color: 'error' };
    const flight = inFlightVersion(item);
    const active = activeVersion(item);
    if (flight?.state === 'pending_approval') return { label: 'Aguardando aprovação', color: 'warning' };
    if (flight?.state === 'scheduled') return { label: 'Agendado', color: 'info' };
    if (flight?.state === 'rejected') return { label: 'Rejeitado', color: 'error' };
    if (flight?.state === 'draft') return active ? { label: 'Ativo, com rascunho aberto', color: 'primary' } : { label: 'Rascunho', color: 'default' };
    return active ? { label: 'Ativo', color: 'success' } : { label: 'Sem versão publicada', color: 'default' };
};

// ---- diff ------------------------------------------------------------------------------------------------------------------

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const flatten = (value, prefix = '', out = {}) => {
    if (isPlainObject(value)) {
        Object.keys(value).forEach((key) => flatten(value[key], prefix ? `${prefix}.${key}` : key, out));
    } else if (prefix) {
        out[prefix] = value;
    }
    return out;
};

// Listas de valores simples (canais) comparam como conjunto: mudar só a ordem não é mudança.
const canonical = (value) => (Array.isArray(value) && value.every((x) => typeof x !== 'object')
    ? JSON.stringify([...value].map(String).sort())
    : JSON.stringify(value ?? null));

/**
 * Diferenças entre dois conteúdos (objetos), ordenadas pelo caminho. `consent` marca as que tocam atributos de consentimento, que a tela destaca
 * porque exigem o editor de consentimento e merecem atenção do aprovador.
 */
export const diffContent = (before, after) => {
    const a = flatten(before ?? {});
    const b = flatten(after ?? {});
    const paths = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
    return paths
        .filter((path) => canonical(a[path]) !== canonical(b[path]))
        .map((path) => ({ path, before: a[path], after: b[path], consent: CONSENT_ATTRIBUTES.includes(path.split('.')[0]) }));
};

export const consentChanges = (before, after) => diffContent(before, after).filter((d) => d.consent);

// ---- ações permitidas -------------------------------------------------------------------------------------------------------

/**
 * O que a pessoa pode fazer com uma versão. `ctx` = { canEdit, canApprove, myId }. É a MESMA regra do servidor, só para esconder botões e
 * explicar o motivo; se divergir, o servidor decide (e a tela mostra o erro dele).
 */
export const versionActions = (version, item, ctx) => {
    const flight = inFlightVersion(item);
    const isAuthor = !!ctx.myId && (version.authorId === ctx.myId || version.lastEditorId === ctx.myId);
    const pending = version.state === 'pending_approval';

    return {
        edit: ctx.canEdit && IN_FLIGHT_STATES.includes(version.state),
        startNew: ctx.canEdit && version.id === item.activeVersionId && !flight && !item.isDeactivated,
        request: ctx.canEdit && version.state === 'draft',
        approve: ctx.canApprove && pending && !isAuthor,
        reject: ctx.canApprove && pending,
        blockedApprovalReason: ctx.canApprove && pending && isAuthor ? 'Você é o autor ou o último editor desta versão: outra pessoa precisa aprová-la.' : null,
        cancelSchedule: ctx.canApprove && version.state === 'scheduled',
        revert: ctx.canApprove && version.state === 'superseded',
        // Teste do rascunho: só de tipo, só do que ainda não foi ao ar e só se algum canal testável estiver ligado. Quem vê o catálogo testa.
        test: item.kind === 'type' && IN_FLIGHT_STATES.includes(version.state) && testableChannels(version).length > 0
    };
};

export const canDeactivate = (item, ctx) => ctx.canApprove && !!item.activeVersionId && !item.isDeactivated;

// ---- envio de teste (C6) ----------------------------------------------------------------------------------------------------

export const TEST_CHANNEL_LABELS = { email: 'E-mail', push: 'Push' };

/** Os canais do rascunho que o teste cobre (WhatsApp e no app ficam de fora nesta fase). */
export function testableChannels(version) {
    const channels = Array.isArray(version?.content?.channels) ? version.content.channels : [];
    return Object.keys(TEST_CHANNEL_LABELS).filter((c) => channels.includes(c));
}

const shortProfile = (id) => `${id.slice(0, 8)}…`;

/** Os destinos permitidos no canal, como opções {value, label}. A lista vem da configuração do servidor; a tela só a mostra. */
export function testDestinations(targets, channel) {
    if (!targets) return [];
    if (channel === 'email') return (targets.emails ?? []).map((value) => ({ value, label: value }));
    if (channel === 'push') return (targets.pushProfiles ?? []).map((value) => ({ value, label: `Perfil ${shortProfile(value)}` }));
    return [];
}

/** Por que não dá para testar neste canal (ou null se dá). */
export function testBlockedReason(targets, channel) {
    if (!targets) return 'Carregando os destinos de teste...';
    if (!targets.enabled) return 'O envio de teste não está configurado neste ambiente.';
    if (testDestinations(targets, channel).length === 0) {
        return channel === 'email'
            ? 'Nenhum e-mail está na lista de destinos de teste. A lista é configuração do serviço.'
            : 'Nenhum perfil está na lista de destinos de teste. A lista é configuração do serviço.';
    }
    return null;
}

/** Como mostrar o que o servidor respondeu ao teste. */
export function describeTestResult(result, channel = 'email') {
    if (result?.sent) {
        const where = channel === 'push' ? 'Confira o aparelho em que esse perfil ativou as notificações.' : 'Confira a caixa de entrada (e o spam).';
        return { severity: 'success', message: `Teste enviado: "${result.subject}". ${where}` };
    }
    return { severity: 'warning', message: `O teste não saiu${result?.detail ? `: ${result.detail}` : '.'}` };
}

// ---- conteúdo -------------------------------------------------------------------------------------------------------------

/** Ids dos grupos de limite que o motor enxerga (publicados e ativos): é a lista de onde um tipo pode escolher o seu grupo. */
export const publishedGroupKeys = (items) => items
    .filter((i) => i.kind === 'group' && !i.isDeactivated && i.activeVersionId)
    .map((i) => i.key)
    .sort();

/** Lê o texto do editor. Devolve { value } ou { error } com uma mensagem para a pessoa. */
export const parseContent = (text) => {
    try {
        const value = JSON.parse(text);
        if (!isPlainObject(value)) return { error: 'O conteúdo deve ser um objeto JSON (entre chaves).' };
        return { value };
    } catch (e) {
        return { error: `JSON inválido: ${e.message}` };
    }
};

export const KEY_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

export const formatValue = (value) => {
    if (value === undefined) return '(não existia)';
    if (value === null) return '(vazio)';
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
};
